import React, { useState, useCallback, useEffect } from 'react'
import QuizContent from '../shared/QuizContent'
import QuizTitle from '../shared/QuizTitle'
import QuizOptions from '../shared/QuizOptions'
import QuizOption from '../shared/QuizOption'
import PlayPauseIcon from '../shared/PlayPauseIcon'
import { QUIZ_OPTIONS_DATA } from '../../constants'
import stepSisSvg from '../../assets/StepSis.svg'
import stepMomSvg from '../../assets/StepMom.svg'
import girlfriendSvg from '../../assets/Girlfriend.svg'
import sexFriendSvg from '../../assets/SexFriend.svg'
import bestFriendSvg from '../../assets/bestFriend.svg'
import closeFriendSvg from '../../assets/closeFriend.svg'
import caregiverSvg from '../../assets/Caregiver.svg'
import jesterSvg from '../../assets/Jester.svg'
import innocentSvg from '../../assets/Innocent.svg'
import { loadCharacterAttributes } from '../../utils/api.js'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { trackStep5 } from '../../utils/analytics'

const QUIZ_PERSONALITY_TRAITS = [
  { value: 'caregiver', label: 'Sweet', icon: caregiverSvg },
  { value: 'jester', label: 'Sarcastic', icon: jesterSvg },
  { value: 'innocent', label: 'Geeky', icon: innocentSvg },
]

// /quiz relationships — values are quiz-only and mapped to backend labels in api.js (relationshipLabelMap).
// Backend currently has only 'Best Friend'; 'Gamer Buddy' and 'Close Friend' need to be added on the backend.
const QUIZ_RELATIONSHIPS = [
  { value: 'gamerBuddy', label: 'Gamer Buddy', icon: stepSisSvg },
  { value: 'closeFriend', label: 'Close Friend', icon: closeFriendSvg },
  { value: 'bestFriend', label: 'Best Friend', icon: bestFriendSvg },
]

const Step4 = ({ onAnswer, onPrevious }) => {
  const [selectedVoice, setSelectedVoice] = useState('')
  const [selectedPersonality, setSelectedPersonality] = useState('')
  const [selectedPersonalityTrait, setSelectedPersonalityTrait] = useState('')
  const [playingVoice, setPlayingVoice] = useState(null)
  const [attributesMap, setAttributesMap] = useState({})
  const location = useLocation()
  const audioRefs = React.useRef({})
  const isHalloween = !!location.pathname.includes('/halloween')
  const isQuiz = !!location.pathname.includes('/quiz')

  const voices = QUIZ_OPTIONS_DATA.voice
  const personalities = isQuiz ? QUIZ_RELATIONSHIPS : QUIZ_OPTIONS_DATA.personality
  const personalityTraits = QUIZ_PERSONALITY_TRAITS

  useEffect(() => {
    ;(async () => {
      const map = await loadCharacterAttributes()
      setAttributesMap(map)
    })()
  }, [])

  // Auto-select first options on mount
  useEffect(() => {
    if (voices && voices.length > 0 && !selectedVoice) {
      setSelectedVoice(voices[0].value)
    }
  }, [voices, selectedVoice])

  useEffect(() => {
    if (personalities && personalities.length > 0 && !selectedPersonality) {
      setSelectedPersonality(personalities[0].value)
    }
  }, [personalities, selectedPersonality])

  useEffect(() => {
    if (isQuiz && personalityTraits.length > 0 && !selectedPersonalityTrait) {
      setSelectedPersonalityTrait(personalityTraits[0].value)
    }
  }, [isQuiz, personalityTraits, selectedPersonalityTrait])

  const handlePersonalityTraitClick = useCallback(value => {
    setSelectedPersonalityTrait(value)
  }, [])

  const getAttributeId = (type, label) => {
    if (!attributesMap[type] || !label) return null
    return attributesMap[type][label.trim()] || null
  }

  const handleVoiceClick = useCallback(
    value => {
      setSelectedVoice(value)
      const id = getAttributeId('voice', value)
      console.log('📝 BodyType answer:', value)
      console.log('🔗 API ID:', id)
    },
    [attributesMap], // eslint-disable-line react-hooks/exhaustive-deps
  )

  const handlePersonalityClick = useCallback(
    value => {
      setSelectedPersonality(value)
      const id = getAttributeId('relationship', value)
      console.log('📝 BodyType answer:', value)
      console.log('🔗 API ID:', id)
    },
    [attributesMap], // eslint-disable-line react-hooks/exhaustive-deps
  )

  const handlePlayVoice = useCallback(
    (voice, event) => {
      event?.stopPropagation()

      const audio = audioRefs.current[voice.value]
      if (audio) {
        if (playingVoice === voice.value) {
          // Stop playing
          audio.pause()
          audio.currentTime = 0
          setPlayingVoice(null)
        } else {
          // Start playing
          setPlayingVoice(voice.value)
          audio.play()

          audio.onended = () => {
            setPlayingVoice(null)
          }
        }
      }
    },
    [playingVoice],
  )

  const handleContinue = useCallback(() => {
    const voiceOption = voices.find(v => v.value === selectedVoice)
    const traitOption = personalityTraits.find(p => p.value === selectedPersonalityTrait)
    const personalityOption = personalities.find(p => p.value === selectedPersonality)

    trackStep5(selectedVoice, selectedPersonality)

    const payload = {
      personality: selectedPersonality,
    }
    if (!isQuiz) {
      payload.voice = selectedVoice
      payload.voiceAudio = voiceOption?.audio || null
    }
    if (isQuiz) {
      payload.personalityLabel = personalityOption?.label || null
      payload.personalityTrait = selectedPersonalityTrait
      payload.personalityTraitLabel = traitOption?.label || null
    }
    onAnswer('step4', payload)
  }, [selectedVoice, selectedPersonality, selectedPersonalityTrait, voices, personalities, personalityTraits, isQuiz, onAnswer])

  // Allow continue if all selections are made
  const canContinue = selectedPersonality && (isQuiz ? selectedPersonalityTrait : selectedVoice)

  const { t } = useTranslation()

  // Render personality icon
  const renderPersonalityIcon = personalityValue => {
    if (isQuiz) {
      // /quiz route — icons supplied by QUIZ_RELATIONSHIPS so the relationships block can stay value-agnostic
      const option = personalities.find(p => p.value === personalityValue)
      if (!option?.icon) return null
      return (
        <div className='icon-badge'>
          <img src={option.icon} alt={option.label || personalityValue} />
        </div>
      )
    }
    const PERSONALITY_ICONS = {
      stepSis: { src: stepSisSvg, alt: 'Step Sister' },
      stepMom: { src: stepMomSvg, alt: 'Step Mom' },
      girlfriend: { src: girlfriendSvg, alt: 'Girlfriend' },
      sexFriend: { src: sexFriendSvg, alt: 'Sex Friend' },
    }
    const entry = PERSONALITY_ICONS[personalityValue]
    if (!entry) return null
    return (
      <div className='icon-badge'>
        <img src={entry.src} alt={entry.alt} />
      </div>
    )
  }

  return (
    <div className='quizContentWrapper'>
      <QuizContent className='step4-design'>
        {/* Personality Block — /quiz only */}
        {isQuiz && (
          <div className='step4-block'>
            <QuizTitle>{t('choose_personality')}</QuizTitle>
            <QuizOptions className='voice-options'>
              {personalityTraits.map(trait => (
                <QuizOption
                  key={trait.value}
                  value={trait.value}
                  name='personalityTrait'
                  onClick={handlePersonalityTraitClick}
                  className={`personality-option ${selectedPersonalityTrait === trait.value ? 'selected' : selectedPersonalityTrait ? 'not-selected' : ''}`}
                >
                  <div className='personality-item'>
                    <div className='personality-icon'>
                      <div className='icon-badge'>
                        <img src={trait.icon} alt={trait.label} />
                      </div>
                    </div>
                    <div className='personality-info'>
                      <div className='personality-title'>{trait.label}</div>
                    </div>
                  </div>
                </QuizOption>
              ))}
            </QuizOptions>
          </div>
        )}

        {/* Voice Block — hidden on /quiz (no voice selection there) */}
        {!isQuiz && (
          <div className='step4-block'>
            <QuizTitle>
              {isHalloween ? (
                <>
                  {t('choose')}
                  <span className='specialText'> {t('voice_text')}</span>
                </>
              ) : (
                t('choose_voice')
              )}
            </QuizTitle>
            <QuizOptions className='voice-options'>
              {voices.map(voice => (
                <QuizOption
                  key={voice.value}
                  value={voice.value}
                  name='voice'
                  onClick={handleVoiceClick}
                  className={`voice-option ${selectedVoice === voice.value ? 'selected' : selectedVoice ? 'not-selected' : ''}`}
                >
                  <div className='voice-card'>
                    <div className='voice-text'>
                      <div className='voice-title'>{t(voice.text)}</div>
                    </div>
                    <div className='voice-controls'>
                      <button className='play-button' onClick={e => handlePlayVoice(voice, e)} type='button'>
                        <PlayPauseIcon isPlaying={playingVoice === voice.value} />
                      </button>
                    </div>
                  </div>
                  <audio ref={el => (audioRefs.current[voice.value] = el)} src={voice.audio} preload='metadata' />
                </QuizOption>
              ))}
            </QuizOptions>
          </div>
        )}

        {/* Relationships Block */}
        <div className='step4-block'>
          <QuizTitle>
            {isHalloween ? (
              <>
                {t('choose')}
                <span className='specialText'> {t('relationships_text')}</span>
              </>
            ) : (
              t('choose_relationships')
            )}
          </QuizTitle>
          <QuizOptions className='voice-options'>
            {personalities.map(personality => (
              <QuizOption
                key={personality.value}
                value={personality.value}
                name='personality'
                onClick={handlePersonalityClick}
                className={`personality-option ${selectedPersonality === personality.value ? 'selected' : selectedPersonality ? 'not-selected' : ''}`}
              >
                <div className='personality-item'>
                  <div className='personality-icon'>{renderPersonalityIcon(personality.value)}</div>
                  <div className='personality-info'>
                    <div className='personality-title'>
                      {isQuiz
                        ? (personality.label || '').split(' ').map((word, idx, arr) => (
                            <React.Fragment key={idx}>
                              {word}
                              {idx < arr.length - 1 && <br />}
                            </React.Fragment>
                          ))
                        : t(personality.text)}
                    </div>
                  </div>
                </div>
              </QuizOption>
            ))}
          </QuizOptions>
        </div>

        <div className='navigation-wrapper'>
          <button className='previous-button' onClick={onPrevious}>
            {t('previous')}
          </button>
          <button className='continue-button' onClick={handleContinue} disabled={!canContinue}>
            {t('next')}
          </button>
        </div>
      </QuizContent>
    </div>
  )
}

export default Step4
